import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  Alert, Btn, FormField, Tabs,
  inputStyle, selectStyle,
  colors, spacing, typography, radius, shadows,
} from '../design-system';
import axiosClient from '../api/axiosClient';
import { generatePassword } from '../utils/passwordGenerator';

// ── NavCard (mirrors SuperAdminDashboard pattern) ──────────────────────────────

function NavCard({ icon, label, description, to, accentColor }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const accent = accentColor || colors.primary;
  return (
    <div
      onClick={() => navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: colors.surface,
        border: `1px solid ${hovered ? accent : colors.border}`,
        borderRadius: radius.lg,
        padding: `${spacing.md} ${spacing.lg}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        transition: 'all 150ms ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? shadows.md : shadows.cardShadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: accent }} />
      <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, lineHeight: 1.2 }}>{label}</div>
        {description && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</div>
        )}
      </div>
      <span style={{ color: hovered ? accent : colors.textMuted, fontSize: '14px', flexShrink: 0 }}>›</span>
    </div>
  );
}

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const INDUSTRIES = [
  'Manufacturing', 'Construction', 'Healthcare', 'Education', 'Finance',
  'Retail', 'Technology', 'Transport & Logistics', 'Food & Beverage',
  'Energy & Utilities', 'Professional Services', 'Other',
];

const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

export default function AdminDashboard() {
  const user     = JSON.parse(localStorage.getItem('user') || '{}');
  const token    = localStorage.getItem('token');
  const headers  = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const isCB     = user.role === 'certification_body';

  const [clients, setClients]           = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [invoices, setInvoices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('overview');
  const [showCreate, setShowCreate]     = useState(false);
  const [showCreds, setShowCreds]       = useState(null);
  const [saving, setSaving]             = useState(false);
  const [msg, setMsg]                   = useState('');
  const [msgType, setMsgType]           = useState('success');

  const [form, setForm] = useState({
    company_name: '', industry: '', size: '', country: '',
    user_email: '', user_username: '', user_full_name: '',
    user_password: generatePassword(),
  });

  const flash = (m, t = 'success') => {
    setMsg(m); setMsgType(t);
    setTimeout(() => setMsg(''), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, certsRes, invRes] = await Promise.all([
        fetch(`${BASE}/clients/`, { headers }),
        fetch(`${BASE}/certifications/certifications/`, { headers }),
        fetch(`${BASE}/invoices/`, { headers }),
      ]);
      setClients(clientsRes.ok ? (d => Array.isArray(d) ? d : [])(await clientsRes.json()) : []);
      setCertificates(certsRes.ok ? (d => Array.isArray(d) ? d : [])(await certsRes.json()) : []);
      setInvoices(invRes.ok ? (d => Array.isArray(d) ? d : [])(await invRes.json()) : []);
    } catch {
      setClients([]); setCertificates([]); setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/clients/`, {
        method: 'POST', headers,
        body: JSON.stringify({
          company_name:   form.company_name,
          industry:       form.industry   || null,
          size:           form.size       || null,
          country:        form.country    || null,
          user_email:     form.user_email,
          user_username:  form.user_username,
          user_password:  form.user_password,
          user_full_name: form.user_full_name || null,
        }),
      });
      if (!res.ok) {
        let detail = 'Failed to create client';
        try { const d = await res.json(); detail = d.detail || detail; } catch {}
        throw new Error(detail);
      }
      const created = await res.json();
      setShowCreate(false);
      setShowCreds({ company: created.company_name, email: created.user_email, username: created.user_username, password: form.user_password });
      setForm({ company_name: '', industry: '', size: '', country: '', user_email: '', user_username: '', user_full_name: '', user_password: generatePassword() });
      fetchData();
    } catch (err) {
      flash('Error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = isCB ? 'Certification Body' : 'Administrator';

  const kpis = [
    { label: 'Clients',           value: clients.length,                                            icon: '🏢', color: colors.primary },
    { label: 'Certificates',      value: certificates.length,                                       icon: '📄', color: colors.success },
    { label: 'Active Certs',      value: certificates.filter(c => c.status === 'issued').length,    icon: '✅', color: colors.info },
    { label: 'Pending',           value: certificates.filter(c => c.status === 'pending').length,   icon: '⏳', color: colors.warning },
  ];

  const clientColumns = [
    { key: 'name',                  label: 'Company',    render: v => <strong style={{ color: colors.textPrimary }}>{v}</strong> },
    { key: 'industry',              label: 'Industry',   render: v => v || '—' },
    { key: 'size',                  label: 'Size',       render: v => v || '—' },
    { key: 'user_count',            label: 'Users',      render: v => v ?? 0 },
    { key: 'implementation_status', label: 'Status',     render: v => <StatusBadge status={v || 'not_set'} /> },
    { key: 'created_at',            label: 'Added',      render: v => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  const certColumns = [
    { key: 'certificate_number', label: 'Cert #',    render: v => <span style={{ fontFamily: 'monospace', fontSize: typography.fontSizes.sm }}>{v}</span> },
    { key: 'company_name',       label: 'Company',   render: v => v || '—' },
    { key: 'iso_standard_code',  label: 'Standard',  render: v => v || '—' },
    { key: 'certificate_type',   label: 'Type',      render: v => v ? v.replace('_', ' ') : '—' },
    { key: 'status',             label: 'Status',    render: v => <StatusBadge status={v} /> },
  ];

  // Invoice financial summary
  const totalOwed     = invoices.filter(i => i.billed_to_company_id === user.company_id).reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid     = invoices.filter(i => i.billed_to_company_id === user.company_id && i.payment_status === 'paid').reduce((s, i) => s + (i.amount || 0), 0);
  const totalPending  = invoices.filter(i => i.billed_to_company_id === user.company_id && i.payment_status === 'pending').reduce((s, i) => s + (i.amount || 0), 0);
  const totalReceived = invoices.filter(i => i.billed_by_company_id === user.company_id && i.payment_status === 'paid').reduce((s, i) => s + (i.amount || 0), 0);

  const invoiceColumns = [
    { key: 'invoice_number',         label: 'Invoice #',  render: v => <span style={{ fontFamily: 'monospace', fontSize: typography.fontSizes.sm }}>{v}</span> },
    { key: 'invoice_type',           label: 'Type',       render: v => v ? v.toUpperCase() : '—' },
    { key: 'billed_to_company_name', label: 'Billed To',  render: v => v || '—' },
    { key: 'billed_by_company_name', label: 'Billed By',  render: v => v || '—' },
    { key: 'amount',                 label: 'Amount',     render: v => v != null ? `£${Number(v).toFixed(2)}` : '—' },
    { key: 'payment_status',         label: 'Status',     render: v => <StatusBadge status={v || 'pending'} /> },
    { key: 'due_date',               label: 'Due',        render: v => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  const tabs = [
    { key: 'overview',      label: 'Overview' },
    { key: 'clients',       label: `Clients (${clients.length})` },
    { key: 'certificates',  label: `Certificates (${certificates.length})` },
    { key: 'financials',    label: `Financials (${invoices.length})` },
    { key: 'portal',        label: 'My Portal' },
  ];

  return (
    <div>
      <PageHeader
        title={user.full_name || 'Dashboard'}
        subtitle={`${roleLabel} Portal — ${user.company_name || ''}`}
        actions={<Btn onClick={() => setShowCreate(true)}>+ Add Client</Btn>}
      />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg('')} />}

      <KPIGrid kpis={kpis} columns={4} />

      <div style={{ marginTop: spacing.xl }}>
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        <div style={{ marginTop: spacing.lg }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
              <div>
                <h3 style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>Recent Clients</h3>
                <DataTable columns={clientColumns.slice(0, 3)} rows={clients.slice(0, 5)} loading={loading} emptyMessage="No clients yet." keyField="id" />
              </div>
              <div>
                <h3 style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>Recent Certificates</h3>
                <DataTable columns={certColumns.slice(0, 3)} rows={certificates.slice(0, 5)} loading={loading} emptyMessage="No certificates yet." keyField="id" />
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <DataTable columns={clientColumns} rows={clients} loading={loading} emptyMessage="No clients found. Add your first client above." keyField="id" />
          )}

          {activeTab === 'certificates' && (
            <DataTable columns={certColumns} rows={certificates} loading={loading} emptyMessage="No certificates found." keyField="id" />
          )}

          {activeTab === 'financials' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              <KPIGrid columns={4} kpis={[
                { label: 'Total Invoiced',  value: `£${totalOwed.toFixed(2)}`,     color: colors.primary,  subtitle: 'amounts billed to you' },
                { label: 'Paid',            value: `£${totalPaid.toFixed(2)}`,     color: colors.success,  subtitle: 'settled invoices' },
                { label: 'Outstanding',     value: `£${totalPending.toFixed(2)}`,  color: colors.warning,  subtitle: 'awaiting payment' },
                { label: 'Revenue Billed',  value: `£${totalReceived.toFixed(2)}`, color: colors.info,     subtitle: 'invoiced to clients' },
              ]} />
              <DataTable
                columns={invoiceColumns}
                rows={invoices}
                loading={loading}
                emptyMessage="No invoices found for your account."
                keyField="id"
              />
            </div>
          )}

          {activeTab === 'portal' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: spacing.md, marginTop: spacing.sm }}>
              <NavCard icon="🏛️" label="My Accreditation"      description="Your CB accreditation status and details"       to="/my-accreditation"        accentColor={colors.primary} />
              <NavCard icon="📊" label="CB Accred. Status"      description="View accreditation progress and conditions"    to="/cb-accreditation-status"  accentColor="#7c3aed" />
              <NavCard icon="🔎" label="Reviewer Dashboard"     description="Course submission review queue"               to="/reviewer-dashboard"       accentColor={colors.info} />
              <NavCard icon="📜" label="Certificate Manager"    description="Issue and manage ISO certificates"            to="/certificates"             accentColor={colors.success} />
              <NavCard icon="👥" label="Clients"                description="Manage your client organisations"             to="/clients"                  accentColor="#059669" />
              <NavCard icon="📖" label="My Courses"             description="Learning modules assigned to you"             to="/my-courses"               accentColor={colors.warning} />
              {isCB && <NavCard icon="✅" label="Allowed Courses"  description="Courses approved for your CB"             to="/allowed-courses"          accentColor="#f59e0b" />}
              <NavCard icon="🎫" label="Support Tickets"        description="Raise and track support requests"            to="/tickets"                  accentColor={colors.textMuted} />
              <NavCard icon="👤" label="My Profile"             description="Account settings and preferences"            to="/profile"                  accentColor={colors.textMuted} />
            </div>
          )}
        </div>
      </div>

      {/* Create Client Modal */}
      {showCreate && (
        <StandardModal
          title="Add New Client"
          onClose={() => setShowCreate(false)}
          width={580}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="admin-create-client" loading={saving}>Create Client</Btn>
            </>
          }
        >
          <form id="admin-create-client" onSubmit={handleCreate}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <FormField label="Company Name" required>
                <input required value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Acme Ltd" style={inputStyle} />
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <FormField label="Industry">
                  <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} style={selectStyle}>
                    <option value="">Select...</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </FormField>
                <FormField label="Size">
                  <select value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} style={selectStyle}>
                    <option value="">Select...</option>
                    {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                  </select>
                </FormField>
              </div>

              <FormField label="Country">
                <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="United Kingdom" style={inputStyle} />
              </FormField>

              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.md, color: colors.textMuted, fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, textTransform: 'uppercase' }}>
                Admin User Account
              </div>

              <FormField label="Full Name">
                <input value={form.user_full_name} onChange={e => setForm(f => ({ ...f, user_full_name: e.target.value }))} placeholder="Jane Smith" style={inputStyle} />
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <FormField label="Email" required>
                  <input required type="email" value={form.user_email} onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))} placeholder="jane@company.com" style={inputStyle} />
                </FormField>
                <FormField label="Username" required>
                  <input required value={form.user_username} onChange={e => setForm(f => ({ ...f, user_username: e.target.value }))} placeholder="jane_smith" style={inputStyle} />
                </FormField>
              </div>

              <FormField label="Temporary Password" helper="Auto-generated — you can edit or regenerate">
                <div style={{ display: 'flex', gap: spacing.sm }}>
                  <input value={form.user_password} onChange={e => setForm(f => ({ ...f, user_password: e.target.value }))} style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
                  <button type="button" onClick={() => setForm(f => ({ ...f, user_password: generatePassword() }))}
                    style={{ padding: `0 ${spacing.md}`, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.md, cursor: 'pointer', fontSize: typography.fontSizes.sm, whiteSpace: 'nowrap' }}>
                    Regenerate
                  </button>
                </div>
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Credentials Modal */}
      {showCreds && (
        <StandardModal
          title="Client Created"
          onClose={() => setShowCreds(null)}
          width={460}
          footer={
            <>
              <Btn variant="secondary" onClick={() => navigator.clipboard.writeText(`Company: ${showCreds.company}\nEmail: ${showCreds.email}\nUsername: ${showCreds.username}\nPassword: ${showCreds.password}`)}>Copy All</Btn>
              <Btn onClick={() => setShowCreds(null)}>Done</Btn>
            </>
          }
        >
          <Alert type="success" message={`${showCreds.company} created successfully.`} />
          <div style={{ marginTop: spacing.md, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {[['Company', showCreds.company], ['Email', showCreds.email], ['Username', showCreds.username], ['Password', showCreds.password]].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderRadius: radius.md }}>
                <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{label}</span>
                <code style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{value}</code>
              </div>
            ))}
          </div>
          <p style={{ marginTop: spacing.md, fontSize: typography.fontSizes.xs, color: colors.warning }}>Save these credentials — the password cannot be retrieved later.</p>
        </StandardModal>
      )}
    </div>
  );
}
